import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'utils/modifiedTranslation';
import PropTypes from 'prop-types';
import { PLATFORM, USER_SOURCE } from 'utils/constants/user';
import useRecaptcha from 'utils/hooks/useRecaptcha';
import { isEmailValid } from 'functions/isEmailValid';
import { isPhoneValid } from 'functions/formValidations/isPhoneValid';
import { preProcessCondProfile } from 'modules/user/utils';
import { getIntercomUserCondition } from 'modules/intercom/functions';
import Form, { FORM_INPUT_TYPES } from 'new-ui/Components/Form';
import app from 'new-ui/app';
import { GA_HCP } from 'modules/ga/hcp';
import { signup } from 'new-ui/functions';
import ArrowCircle from 'assets/icons/arrow-circle-right.svg';
import { HCPSignupHeader } from './HCPSignupHeader';
import { HCPSignupFooter } from './HCPSignupFooter';
import { HCPSignupWrapper } from './HCPSignupWrapper';
import { HCPSignupFormWrapper } from './HCPSignupFormWrapper';

const getForm = (indication, t) => ({
  data: {
    email: '',
    phone: '',
    role: 'patient',
    indication,
  },
  inputs: {
    email: {
      type: FORM_INPUT_TYPES.INPUT,
      required: true,
      icon: 'email',
      title: t('hcp_signup.form.input.email'),
    },
    phone: {
      type: FORM_INPUT_TYPES.PHONE,
      icon: 'phone',
      title: t('hcp_signup.form.input.phone'),
      placeholder: '',
    },
    indication: {
      required: true,
      title: t('add_patient.indication_input_title'),
      type: FORM_INPUT_TYPES.SEARCH_INDICATION_CHOOSER,
    },
  },
});

const validateForm = (form, t) => {
  if (!isEmailValid(form.data.email)) {
    return t('hcp_signup.form.error.email');
  }

  if (!form.data.role) {
    return t('hcp_signup.form.error.role');
  }

  if (form.data.phone && !isPhoneValid(form.data.phone)) {
    return t('hcp_signup.form.error.phone');
  }

  if (!form.data.indication) {
    return t('hcp_signup.form.error.indication');
  }
  return null;
};

// user_type is patient
export const HCPPatientSignup = (props) => {
  const {
    title = 'hcp_signup.patient.title',
    subtitle = 'hcp_signup.patient.subtitle',
    indication,
    profile,
    onSignup,
  } = props;

  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState('');
  const { recaptcha, getRecaptchaTokenAsync } = useRecaptcha();
  const { t } = useTranslation();
  const { cameFrom, urlParameters } = useSelector((state) => ({
    cameFrom: state.main.cameFrom,
    urlParameters: state.main.urlParameters,
  }));

  useEffect(() => {
    setForm(getForm(indication, t));
  }, [indication, t]);

  const handleOnSubmit = async () => {
    const validationError = validateForm(form, t);

    if (validationError) {
      return setFormError(validationError);
    }

    app.showPreloader();

    const {
      indication: formIndication, email, phone, role,
    } = form.data;

    const conditionProfile = await preProcessCondProfile(
      formIndication,
      profile?.condition_profile ?? {},
    );

    const params = {
      email,
      indication: formIndication.value,
      recaptchaToken: await getRecaptchaTokenAsync(),
      condition_profile: conditionProfile,
      info: {
        ...profile?.info ?? {},
        condition: formIndication.value,
        user_type: role,
        phone,
        email,
        platform: PLATFORM.HCP,
      },
      personal: {
        ...profile?.personal ?? {},
        condition: formIndication.value,
        providerId: window._provider || '',
        providerCode: window._provider || '',
      },
      statistic: { cameFrom, urlParameters },
      onsignup: (user) => {
        app.intercom.updateUserAttributes(
          {
            email,
            phone,
            user_type: user?.info?.user_type,
            user_source: USER_SOURCE.HCP,
            organization: '',
            condition: getIntercomUserCondition(formIndication),
          },
          user.info.user_wix_id,
        );

        app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.PATIENT_SIGNUP_SUCCESS);
        app.hidePreloader();

        onSignup();
      },
      onerror: () => {
        setFormError(t('hcp_signup.orm.error.signup'));
        app.hidePreloader();
        app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.PATIENT_SIGNUP_FAILED);
      },
    };

    await signup(params);
  };

  const handleOnChange = () => {
    setFormError('');
  };

  return (
    <HCPSignupWrapper>
      {recaptcha}
      <HCPSignupHeader
        title={(
          <div style={{ lineHeight: 'normal' }}>
            {t(title)}
          </div>
        )}
        subtitle={(
          <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
            {t(subtitle)}
          </div>
        )}
      />
      <HCPSignupFormWrapper error={formError}>
        <Form
          form={form}
          onChange={handleOnChange}
          submit={{
            action: handleOnSubmit,
            icon: ArrowCircle,
            title: t('general.save'),
          }}
        />
      </HCPSignupFormWrapper>
      <HCPSignupFooter />
    </HCPSignupWrapper>
  );
};

HCPPatientSignup.propTypes = {
  title: PropTypes.any,
  subtitle: PropTypes.any,
  indication: PropTypes.shape({
    value: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
  }),
  profile: PropTypes.object,
  onSignup: PropTypes.func,
};
